<template>
  <div>
    <div v-if="hasSubmit">
      <slot name="confirmation">
        <SofieText :value="confirmationText ?? t('sofie.form.confirmation_text')" />
      </slot>
    </div>
    <div v-else-if="forbiddenForm">
      <SofieText :value="$t('sofie.form.private_text')" />
    </div>
    <FormKit
      v-else
      :id="`form-${props.formId}`"
      type="form"
      :preserve="true"
      :disabled="isLoading"
      :plugins="[plugin]"
      @submit="submit"
    >
      <FormKitSchema
        :schema="schema"
        :library="library"
      />
    </FormKit>
  </div>
</template>

<script setup lang="ts">
import type {FormKitNode, FormKitSchemaFormKit, FormKitSchemaNode} from '@formkit/core';
import {type FormVariables, useSofieForm, useSofieFormMutation} from '../composables/form';

const props = defineProps<{
  confirmationText?: string
  formId: number | string
}>();

const {t} = useI18n();
const {
  loading,
  schema,
  plugin,
  library,
  onError: onQueryError,
} = useSofieForm(toRef(() => +props.formId));

const {
  loading: submitting,
  finished: hasSubmit,
  mutate,
  onDone: onSubmitDone,
} = useSofieFormMutation();

const forbiddenForm = ref(false);

function isFormkitSchemaNodeFormkit(node: FormKitSchemaNode | undefined): node is FormKitSchemaFormKit {
  return typeof node === 'object' && '$formkit' in node;
}

function isFileField(fieldName: string): boolean {
  return schema.value.find((f): f is FormKitSchemaFormKit => isFormkitSchemaNodeFormkit(f) && f.name === fieldName)?.$formkit === 'file';
}

function submit(data: Record<string, any>, node: FormKitNode) {
  const formVariables: Omit<FormVariables, 'id'> = {
    data,
    attachments: [],
  };

  Object.keys(data).forEach((fieldName) => {
    if (!isFileField(fieldName) || data[fieldName].length < 1) {
      return;
    }

    formVariables.attachments.push({
      field_name: fieldName,
      attachment: data[fieldName][0].file,
    });

    data[fieldName] = fieldName;
  });

  return mutate({
    id: +props.formId,
    ...formVariables,
  }, node);
}

const isLoading = computed(() => submitting.value || loading.value);

onQueryError((error) => {
  const hasForbidden = error.graphQLErrors?.find(e => e.message === 'Forbidden');

  if (hasForbidden) {
    forbiddenForm.value = true;
  }
});

onSubmitDone(async ({data}) => {
  if (data?.submitForm?.confirm_url) {
    await navigateTo(data.submitForm.confirm_url, {
      external: true,
    });
  }
});
</script>
